import { useCallback, useState } from "react";
import { useUpdateDraftCollectionMutation } from "./useUpsertCollectionMutation";
import {
  ApprovableMarketplaceStatus,
  CollectionReviewStates,
  DraftCollection,
} from "../../../api/collection";
import useApproveCollectionMutation from "./useApproveCollectionMutation";

export type UseCollectionReviewModalProps = {
  onCloseReviewModal: () => void;
  collection: DraftCollection;
};

const useCollectionReviewModal = ({
  onCloseReviewModal,
  collection,
}: UseCollectionReviewModalProps) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [reviewState, setReviewState] = useState<CollectionReviewStates>(
    CollectionReviewStates.Rejected,
  );
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(
    collection.paymentDetails?.planId,
  );
  // Starts empty on every review, so the reviewer states the marketplace
  // decision explicitly instead of inheriting one they never saw.
  const [marketplaceStatus, setMarketplaceStatus] = useState<
    ApprovableMarketplaceStatus | undefined
  >();
  const {
    mutate: approveCollectionMutation,
    isPending: isApprovePending,
    error: approveError,
    reset: resetApprove,
  } = useApproveCollectionMutation();

  const {
    mutate: updateCollectionMutation,
    isPending: isRejectPending,
    error: rejectError,
    reset: resetReject,
  } = useUpdateDraftCollectionMutation();

  const handleFormModalClose = useCallback(() => {
    setIsFormModalOpen(false);
  }, []);

  const handleSubmitReview = useCallback(
    (message: string) => {
      if (reviewState === CollectionReviewStates.Approved) {
        if (!marketplaceStatus) return;
        approveCollectionMutation(
          {
            collectionId: collection.id,
            message,
            planId: selectedPlanId,
            marketplaceStatus,
          },
          {
            onSuccess: () => {
              setIsFormModalOpen(false);
              onCloseReviewModal();
            },
          },
        );
      } else {
        updateCollectionMutation(
          {
            id: collection.id,
            reviewStatus: { state: CollectionReviewStates.Rejected, message },
          },
          {
            onSuccess: () => {
              setIsFormModalOpen(false);
              onCloseReviewModal();
            },
          },
        );
      }
    },
    [
      collection,
      reviewState,
      onCloseReviewModal,
      updateCollectionMutation,
      approveCollectionMutation,
      selectedPlanId,
      marketplaceStatus,
    ],
  );

  // A previous failure keeps its error until the next submit, so clear both
  // mutations when the form modal is (re)opened - otherwise the stale error
  // greets the admin before they submit anything.
  const openFormModal = useCallback(
    (state: CollectionReviewStates) => {
      resetApprove();
      resetReject();
      setReviewState(state);
      setIsFormModalOpen(true);
    },
    [resetApprove, resetReject],
  );

  const handleRejectButtonClick = useCallback(
    () => openFormModal(CollectionReviewStates.Rejected),
    [openFormModal],
  );

  const handleApproveClick = useCallback(
    () => openFormModal(CollectionReviewStates.Approved),
    [openFormModal],
  );

  return {
    isFormModalOpen,
    handleFormModalClose,
    handleSubmitReview,
    handleRejectButtonClick,
    handleApproveClick,
    reviewState,
    selectedPlanId,
    setSelectedPlanId,
    marketplaceStatus,
    setMarketplaceStatus,
    // Approve and reject share the one form modal, so they share its pending
    // and error state - but the error must come from the mutation the current
    // review state actually submits, or the modal blames the wrong action.
    isSubmitPending: isApprovePending || isRejectPending,
    submitError:
      reviewState === CollectionReviewStates.Approved
        ? approveError
        : rejectError,
  };
};
export default useCollectionReviewModal;
